import { useEffect, useState } from "react";
import { Loader2, Check, X, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUS_STYLE = {
  requested: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
  approved: "border-blue-500/30 bg-blue-500/10 text-blue-400",
  refunded: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  rejected: "border-zinc-500/30 bg-zinc-500/10 text-zinc-400",
  failed: "border-red-500/30 bg-red-500/10 text-red-400",
};

function StatusBadge({ status }) {
  return (
    <span data-testid={`refund-status-${status}`}
      className={`rounded border px-2 py-0.5 text-xs font-semibold uppercase ${STATUS_STYLE[status] || STATUS_STYLE.requested}`}>
      {status}
    </span>
  );
}

export default function AdminRefunds() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/refunds").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/refunds/${id}/${action}`, { admin_note: note });
      if (action === "approve" && res.status === "refunded") toast.success("Refund succeeded — access revoked");
      else if (action === "approve" && res.status === "failed") toast.error(`Refund failed: ${res.refund_error || "see details"} — access kept`);
      else if (action === "reject") toast.success("Request rejected — access unchanged");
      setSelected(null); setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (!data) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const items = data.items || [];

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Refund Requests</h1>
        <button onClick={load} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-[#DCA846]" data-testid="refunds-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>
      <p className="mt-2 text-sm text-zinc-500">{data.pending} pending · {data.total} total. Rules: within 7 days of purchase and under 20% completion.</p>

      <div className="mt-6 overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-400">
            <tr>
              <th className="p-3">Student</th><th className="p-3">Course</th><th className="p-3">Amount</th>
              <th className="p-3">Purchased</th><th className="p-3">Requested</th><th className="p-3">Progress</th>
              <th className="p-3">Status</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No refund requests yet.</td></tr> :
              items.map((r) => (
                <tr key={r.id} className="border-t border-white/5" data-testid={`refund-row-${r.id}`}>
                  <td className="p-3"><div className="text-white">{r.user_name || "—"}</div><div className="text-xs text-zinc-500">{r.user_email}</div></td>
                  <td className="p-3">{r.course_title}</td>
                  <td className="p-3">${Number(r.amount || 0).toFixed(2)}</td>
                  <td className="p-3 text-zinc-500">{r.purchased_at ? new Date(r.purchased_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{r.progress_pct}% <span className="text-xs text-zinc-500">({r.completed_lessons}/{r.total_lessons})</span></td>
                  <td className="p-3"><StatusBadge status={r.status} /></td>
                  <td className="p-3">
                    <Button size="sm" variant="outline" onClick={() => { setSelected(r); setNote(r.admin_note || ""); }}
                      className="border-white/15 text-white" data-testid={`refund-view-${r.id}`}>View</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="refund-detail-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-lg rounded-lg border border-white/10 bg-zinc-950 p-6">
            <button onClick={() => setSelected(null)} className="absolute right-4 top-4 text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
            <h2 className="font-display text-xl font-bold text-white">Refund Request</h2>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Student" value={`${selected.user_name || "—"} (${selected.user_email})`} />
              <Row label="Course" value={selected.course_title} />
              <Row label="Order ID" value={selected.order_id} mono />
              <Row label="Payment intent" value={selected.stripe_payment_intent_id || "—"} mono />
              <Row label="Amount" value={`$${Number(selected.amount || 0).toFixed(2)} ${(selected.currency || "usd").toUpperCase()}`} />
              <Row label="Purchased" value={selected.purchased_at ? new Date(selected.purchased_at).toLocaleString() : "—"} />
              <Row label="Requested" value={new Date(selected.created_at).toLocaleString()} />
              <Row label="Completion" value={`${selected.progress_pct}% (${selected.completed_lessons}/${selected.total_lessons} lessons)`} />
              <div><span className="text-zinc-500">Status: </span><StatusBadge status={selected.status} /></div>
              {selected.refund_error && <p className="text-red-400">Error: {selected.refund_error}</p>}
              {selected.refund_id && <Row label="Stripe refund" value={selected.refund_id} mono />}
              <div>
                <p className="text-zinc-500">Student reason:</p>
                <p className="mt-1 rounded border border-white/10 bg-zinc-900/50 p-2 text-zinc-300">{selected.reason || "—"}</p>
              </div>
            </div>

            {(selected.status === "requested" || selected.status === "failed") ? (
              <>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)"
                  className="mt-4 bg-zinc-900 border-white/10 text-white" data-testid="refund-admin-note" />
                <div className="mt-4 flex gap-3">
                  <Button onClick={() => act(selected.id, "approve")} disabled={busy}
                    className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="refund-approve-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" /> Approve & Refund</>}
                  </Button>
                  <Button onClick={() => act(selected.id, "reject")} disabled={busy} variant="outline"
                    className="flex-1 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="refund-reject-btn">
                    <X className="mr-1 h-4 w-4" /> Reject
                  </Button>
                </div>
                <p className="mt-2 text-xs text-zinc-500">Approving initiates the Stripe refund. Course access is revoked only if Stripe confirms success.</p>
              </>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">This request is {selected.status} and cannot be changed.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return <div><span className="text-zinc-500">{label}: </span><span className={mono ? "font-mono text-xs text-zinc-300" : "text-white"}>{value}</span></div>;
}
